import React from 'react'
import { MessageCircle, Mail, MessageSquare, Sparkles } from 'lucide-react'
import { Channel } from './types'

const CHANNELS: { key: Channel; label: string; icon: typeof MessageCircle }[] = [
    { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
    { key: 'sms', label: 'SMS', icon: MessageSquare },
    { key: 'email', label: 'Email', icon: Mail },
]

interface NotificationComposerProps {
    activeTab: Channel
    onTabChange: (channel: Channel) => void
    recipient: string
    currentMessage: string
    loading: boolean
    error: string | null
    sent: boolean
    onRegenerate: () => void
    onSend: () => void
}

export default function NotificationComposer({
    activeTab,
    onTabChange,
    recipient,
    currentMessage,
    loading,
    error,
    sent,
    onRegenerate,
    onSend,
}: NotificationComposerProps) {
    const activeChannel = CHANNELS.find((c) => c.key === activeTab)
    const ActiveIcon = activeChannel?.icon ?? MessageCircle

    return (
        <div>
            <div className="flex items-baseline justify-between mb-2">
                <p className="text-[12.5px] font-semibold text-slate-900">Send notification to customer</p>
                <span className="text-[11px] text-slate-400">{recipient}</span>
            </div>

            <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-2.5">
                {CHANNELS.map(({ key, label, icon: Icon }) => (
                    <button
                        key={key}
                        onClick={() => onTabChange(key)}
                        className={`flex-1 flex items-center justify-center gap-1.5 text-[12px] font-medium py-1.5 rounded-md transition-colors ${
                            activeTab === key ? 'bg-white text-indigo-950 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        <Icon size={13} strokeWidth={2} />
                        {label}
                    </button>
                ))}
            </div>

            <div className="rounded-xl bg-emerald-50/70 border border-emerald-100 p-3.5 min-h-[92px] flex flex-col justify-between">
                {loading ? (
                    <div className="flex items-center gap-2 text-[12.5px] text-slate-500 py-4 justify-center">
                        <Sparkles size={13} className="animate-pulse" />
                        Drafting {activeTab} message...
                    </div>
                ) : (
                    <p className="text-[12.5px] text-slate-700 leading-relaxed whitespace-pre-line">{currentMessage}</p>
                )}
            </div>

            <div className="flex items-center justify-between mt-2">
                {error ? <p className="text-[11px] text-red-500">{error}</p> : <span className="text-[11px] text-slate-400">Drafted for this order</span>}
                <button
                    onClick={onRegenerate}
                    disabled={loading}
                    className="flex items-center gap-1 text-[11px] font-medium text-indigo-700 hover:text-indigo-900 disabled:opacity-40"
                >
                    <Sparkles size={12} />
                    Regenerate
                </button>
            </div>

            <button
                onClick={onSend}
                disabled={loading}
                className="w-full mt-3 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-[13.5px] font-semibold py-2.5 rounded-xl transition-colors"
            >
                <ActiveIcon size={15} />
                {sent ? 'Sent' : `Send via ${activeChannel?.label ?? ''}`}
            </button>
        </div>
    )
}
